import { DocumentReference } from 'firebase/firestore'
import db from '../../../../db'
import { createRef } from '../../../../db/connection'
import { Collaboration, Room } from '../../../models/Chat'
import Message from '../../../models/Message'
import Customer from '../../../models/Customer'
import User from '../../../models/User'
import * as lc from '../../localstorage'
import * as userAct from '../../../../actions/user'
import * as CustomerService from '../../../../db/serviceCustomer'
import * as UserService from '../../../../db/serviceUser'
import * as AccountService from '../../../../db/serviceAccount'
import * as ChatMessage from '../../../../db/serviceMessage'
import CollaborationRepository from '../repository/collaborationRepository'
import RoomRepository from '../repository/roomRepository'
import {
  ICollaborationService,
  EnrichedCollaboration,
  EnrichedRoom,
} from './ICollaborationService'

/**
 * Collaboration Service Implementation
 * Contains business logic for collaboration operations
 * Migrated from src/actions/chat.tsx
 */
class CollaborationServiceImpl implements ICollaborationService {
  private static instance: CollaborationServiceImpl

  static getInstance(): CollaborationServiceImpl {
    if (!this.instance) {
      this.instance = new CollaborationServiceImpl()
    }
    return this.instance
  }

  // ============================================================================
  // FETCH & ENRICHMENT METHODS
  // ============================================================================

  /**
   * Fetch collaboration by ID with full enrichment
   * Migrated from: actions/chat.fetchCollaborationByID
   */
  async fetchCollaborationById(
    collaborationId: string
  ): Promise<EnrichedCollaboration | undefined> {
    const useruid = lc.getItemLC(lc.LCName.UserID)
    const collab = await CollaborationRepository.getById(collaborationId)

    if (!collab) {
      return undefined
    }

    // Enrich customer
    if (collab.customer) {
      const customer = await CustomerService.getByID(collab.customer.id)
      ;(collab as EnrichedCollaboration).customerModel = customer
    }

    // Mark messages as read
    await ChatMessage.unreadMessages(collaborationId)

    // Verify user exists
    const user = await UserService.getCachedUserByID(useruid)
    if (!user) return undefined

    // Fetch and enrich rooms
    const rooms = await this.fetchRoomsByCollaborationId(collaborationId)
    if (!rooms) {
      console.log('room is undefined')
      return undefined
    }

    ;(collab as EnrichedCollaboration).roomsModel = rooms
    return collab as EnrichedCollaboration
  }

  /**
   * Fetch collaboration by user search keys (marketplace)
   * Migrated from: actions/chat.fetchCollaborationByUserSearchKeys
   */
  async fetchCollaborationByUserSearchKeys(
    marketplace: string,
    userSearchKey: string
  ): Promise<EnrichedCollaboration | undefined> {
    const useruid = lc.getItemLC(lc.LCName.UserID)

    const getCollab = await db
      .collection('collaborations')
      .where(`userSearchKeys.${marketplace}`, '==', userSearchKey)
      .get()

    if (getCollab.empty) {
      console.log('collab is undefined')
      return undefined
    }

    const collab = {
      id: getCollab.docs[0].id,
      ...getCollab.docs[0].data(),
    } as EnrichedCollaboration

    const collaborationId = getCollab.docs[0].id

    // Enrich customer
    if (collab.customer) {
      const customer = await CustomerService.getByID(collab.customer.id)
      collab.customerModel = customer
    }

    // Mark messages as read
    await ChatMessage.unreadMessages(collaborationId)

    // Verify user exists
    const user = await UserService.getCachedUserByID(useruid)
    if (!user) return undefined

    // Fetch and enrich rooms
    const rooms = await this.fetchRoomsByCollaborationId(collaborationId)
    if (!rooms) {
      console.log('room is undefined')
      return undefined
    }

    collab.roomsModel = rooms
    return collab
  }

  /**
   * Fetch CIQ by ID
   * Migrated from: actions/chat.fetchCIQByID
   */
  async fetchCIQById(collabId: string): Promise<EnrichedCollaboration | undefined> {
    const collab = await CollaborationRepository.getById(collabId)
    if (!collab) return undefined

    const enrichedCollab = collab as EnrichedCollaboration

    if (enrichedCollab.customer) {
      const customer = await enrichedCollab.customer.get()
      if (customer !== undefined) {
        enrichedCollab.customerModel = customer.data() as Customer
        enrichedCollab.customerModel.id = customer.id
        enrichedCollab.phoneNumber = enrichedCollab.customerModel.phoneNumber
      }
    }

    return enrichedCollab
  }

  /**
   * Fetch "My Chat" collaborations for current user
   * Migrated from: actions/chat.fetchCollaborations
   */
  async fetchMyChats(
    uid: string,
    clientId: string
  ): Promise<EnrichedCollaboration[]> {
    const collaborations = await CollaborationRepository.getByUser(uid, clientId)

    const newCollabs = await Promise.all(
      collaborations.map(async (collab: any) => {
        const refUser = createRef('users', uid)
        const user = await refUser.get()
        const msg = await ChatMessage.unreadMessages(collab.id)
        collab.unreadMessages = msg

        // Enrich customer
        if (collab.customer) {
          const customer = await collab.customer.get()
          if (customer) {
            let customerData = customer.data() as Customer
            if (customer.id && customerData) {
              customerData.id = customer.id
            }
            collab.customerModel = customerData
          }
        }

        // Enrich user
        if (user) {
          let userData = user.data() as User
          if (user.id) userData.id = user.id
          collab.userModel = userData
        }

        // Enrich toUser
        if (collab?.toUser?.length !== 0) {
          let temp: any[] = []
          await Promise.all(
            collab?.toUser?.map(async (item: any) => {
              const user = await UserService.getCachedUserByID(item.id)
              if (user) temp?.push(user)
            }) || []
          )
          collab.toUserData = temp
        }

        // Enrich company
        if (collab.company) {
          const accessibleCompanies = userAct.getAccessibleCompany()
          if (accessibleCompanies && Array.isArray(accessibleCompanies)) {
            const companyAccess = accessibleCompanies.find(
              (company) => company.id === collab.company?.id
            )
            if (companyAccess) {
              collab.companyModel = companyAccess.companyModel
            }
          }
        }

        return collab
      })
    )

    return newCollabs as EnrichedCollaboration[]
  }

  /**
   * Fetch "All Chat" collaborations by company access
   * Migrated from: actions/chat.fetchCollaborationsByAllCompany
   */
  async fetchAllChats(
    companies: Array<DocumentReference>,
    userUid: string
  ): Promise<EnrichedCollaboration[]> {
    const collaborations = await CollaborationRepository.getByAllCompanyUser(
      companies
    )

    const newCollabs = await Promise.all(
      collaborations.map(async (collab: any) => {
        const refUser = createRef('users', userUid)
        const user = await refUser.get()
        const msg = await ChatMessage.unreadMessages(collab.id)
        collab.unreadMessages = msg

        // Enrich customer
        if (collab.customer) {
          const customer = await collab.customer.get()
          if (customer) {
            let customerData = customer.data() as Customer
            if (customer.id && customerData) customerData.id = customer.id
            collab.customerModel = customerData
          }
        }

        // Enrich user
        if (user) {
          let userData = user.data() as User
          if (user.id && userData) userData.id = user.id
          collab.userModel = userData
        }

        // Enrich company
        if (collab.company?.id) {
          const data = userAct.getAccessibleCompany()
          const companyAccess = data?.find(
            (company) => company.id === collab.company.id
          )
          if (companyAccess) collab.companyModel = companyAccess.companyModel
        }

        // Fetch rooms
        const querySnapshot = await db
          .collection('collaborations')
          .doc(collab.id)
          .collection('rooms')
          .get()

        const roomPromises = querySnapshot.docs.map(async (doc: any) => {
          if (!doc.exists) return undefined
          const accountRef = doc.data()?.account?.id
          let accountsModel = null as any

          if (accountRef) {
            const accountDoc = await db.collection('account').doc(accountRef).get()
            if (accountDoc.exists) accountsModel = accountDoc.data()
          }

          return { ...doc.data(), id: doc.id, accountsModel }
        })

        const rooms = await Promise.all(roomPromises)

        // Fetch toUser for each room
        await Promise.all(
          rooms?.map(async (room: any) => {
            if (!room?.toUser) return
            const users = await Promise.all(
              room.toUser.map(async (z: any) => {
                const user = await UserService.getCachedUserByID(z.id)
                return user ? { ...user } : null
              })
            )
            room.toUserData = users.filter(Boolean)
          }) || []
        )

        collab.roomsModel = rooms
        return collab
      })
    )

    return newCollabs as EnrichedCollaboration[]
  }

  /**
   * Fetch friend/internal collaborations
   * Migrated from: actions/chat.fetchFriendCollaborations
   */
  async fetchFriendCollaborations(uid: string): Promise<EnrichedCollaboration[]> {
    try {
      const collaborations = await CollaborationRepository.getFriendCollaborations(
        uid
      )

      const newCollabs = await Promise.all(
        collaborations.map(async (collab) => {
          let user
          if (collab.joinedUsers?.length > 0) {
            for (let i = 0; i < collab.joinedUsers.length; i++) {
              const joinedUser = await collab.joinedUsers[i].get()
              if (joinedUser?.id !== uid) {
                user = joinedUser
                break
              }
            }
          }

          await ChatMessage.userUnreadMessages(collab.id)

          if (user?.id) {
            let userData = user.data() as User
            userData.id = user.id
            try {
              const listFriendCompany =
                (await UserService.getListUserCompanyByID(user.id)) ?? []
              if (!userData.user_companies) userData.user_companies = []
              if (Array.isArray(listFriendCompany)) {
                listFriendCompany.forEach((company) => {
                  userData.user_companies.push(company)
                })
              }
            } catch (error) {
              console.error('Error processing user companies:', error)
            }
          }
          return collab
        })
      )

      return newCollabs as EnrichedCollaboration[]
    } catch (error) {
      console.error('Error in fetchFriendCollaborations:', error)
      return []
    }
  }

  /**
   * Fetch rooms by collaboration ID with enrichment
   * Migrated from: actions/chat.fetchRoomByCollaborationId
   */
  async fetchRoomsByCollaborationId(
    collaborationId: string
  ): Promise<EnrichedRoom[]> {
    const rooms = await RoomRepository.getByCollaborationId(collaborationId)
    if (!rooms) {
      console.log('fetchRoomsByCollaborationId: rooms undefined', collaborationId)
      return []
    }

    const enrichedRooms = await Promise.all(
      rooms.map(async (room) => {
        const enrichedRoom = room as EnrichedRoom

        if (room.account) {
          await AccountService.getAccountByID(room.account.id)
          if (room.toUser) {
            const toUserModel = await Promise.all(
              room.toUser
                .map(async (z: any) => {
                  const user = await UserService.getCachedUserByID(z.id)
                  return user || undefined
                })
                .filter((x) => x)
            )
            enrichedRoom.toUserModel = toUserModel.filter(Boolean) as User[]
          }
        }
        return enrichedRoom
      })
    )

    return enrichedRooms
  }

  /**
   * Fetch user collaboration by ID (for friend chats)
   * Migrated from: actions/chat.fetchUserCollaborationByID
   */
  async fetchUserCollaborationById(
    collabId: string
  ): Promise<EnrichedCollaboration | undefined> {
    try {
      const collab = await CollaborationRepository.getById(collabId)
      if (!collab) return undefined

      await ChatMessage.userUnreadMessages(collab.id!)
      const activeUserId = lc.getItemLC('UID')

      if (collab.joinedUsers) {
        const userDoc = await collab.joinedUsers
          .find((user) => user.id !== activeUserId)
          ?.get()

        if (userDoc) {
          const userData = { ...userDoc.data(), id: userDoc.id } as User
          const listFriendCompany =
            (await UserService.getListUserCompanyByID(userData.id!)) ?? []
          userData.user_companies = [...listFriendCompany]
        }
      }

      return collab as EnrichedCollaboration
    } catch (error) {
      console.log('Error in fetchUserCollaborationById:', error)
      return undefined
    }
  }

  // ============================================================================
  // MUTATION METHODS
  // ============================================================================

  /**
   * Update collaboration customer reference
   * Migrated from: actions/chat.updateCollaborationCustomer
   */
  async updateCollaborationCustomer(
    collab: Collaboration,
    customerId: string
  ): Promise<void> {
    const customerRef = createRef('customers', customerId)
    const customerDoc = await customerRef.get()
    const customerData = customerDoc.data() as Customer
    const profileName =
      customerData.firstName + ' ' + (customerData.lastName ?? '')

    await db.collection('collaborations').doc(collab?.id).update({
      customer: customerRef,
      profileName: profileName,
      updatedAt: new Date(),
    })
  }

  /**
   * Update last interaction metadata
   * Migrated from: actions/chat.updateLastInteraction
   */
  async updateLastInteraction(
    collaborationId: string,
    newMessage: Message,
    roomId?: string
  ): Promise<Collaboration> {
    const refUser = createRef('users', lc.getItemLC('UID'))
    let collab = await createRef('collaborations', collaborationId)
      .get()
      .then(async (doc) => {
        let resp = doc.data() as Collaboration
        resp.id = doc.id
        return resp
      })

    let arrUserUser = collab.toUser
    if (collab.toUser && collab.toUser.length > 0) {
      if (!arrUserUser?.find((user) => user.id === lc.getItemLC('UID'))) {
        arrUserUser?.push(refUser)
      }
    }

    let lastMessage = newMessage.textContent
    if (
      (newMessage.textContent === '' || newMessage.textContent === undefined) &&
      newMessage.filename !== undefined
    ) {
      lastMessage = newMessage.filename
    }

    await db.collection('collaborations').doc(collaborationId).update({
      lastInteractionChannel: newMessage.channel,
      lastInteractionAt: newMessage.createdAt,
      lastInteractionType: newMessage.messageType,
      lastInteractionMessage: lastMessage,
      toUser: arrUserUser,
      updatedAt: new Date(),
    })

    if (roomId) {
      await db
        .collection('collaborations')
        .doc(collaborationId)
        .collection('rooms')
        .doc(roomId)
        .update({
          toUser: arrUserUser,
          updatedAt: new Date(),
          lastCustomerInteractionAt: newMessage.createdAt,
          profileName: collab.profileName,
        })
    }

    return collab
  }

  /**
   * Update last interaction for user chat
   * Migrated from: actions/chat.updateLastInteractionUserChat
   */
  async updateLastInteractionUserChat(
    collaborationId: string,
    newMessage: Message
  ): Promise<Collaboration> {
    let collab = await createRef('collaborations', collaborationId)
      .get()
      .then(async (doc) => {
        let resp = doc.data() as Collaboration
        resp.id = doc.id
        return resp
      })

    let lastMessage = newMessage.textContent
    if (
      (newMessage.textContent === '' || newMessage.textContent === undefined) &&
      newMessage.filename !== undefined
    ) {
      lastMessage = newMessage.filename
    }

    await db.collection('collaborations').doc(collaborationId).update({
      lastInteractionChannel: newMessage.channel,
      lastInteractionAt: newMessage.createdAt,
      lastInteractionType: newMessage.messageType,
      lastInteractionMessage: lastMessage,
      updatedAt: new Date(),
    })

    return collab
  }

  // ============================================================================
  // MESSAGE METHODS
  // ============================================================================

  /**
   * Fetch messages for collaboration
   * Migrated from: actions/chat.fetchMessageCollaboration
   */
  async fetchMessages(
    listMessages: Message[],
    collaborationId: string,
    roomsId: string,
    lastMessage: any,
    selectedChat: string,
    callback: any
  ): Promise<Message[] | undefined> {
    lastMessage = undefined

    if (listMessages?.length > 0) {
      Promise.all(listMessages)
      if (
        listMessages[0].collaborationModel !== undefined &&
        listMessages[0].collaborationModel?.id === selectedChat
      ) {
        return await ChatMessage.fetchMessage(
          collaborationId,
          roomsId,
          lastMessage,
          callback
        ).then(async (messages) => {
          if (!messages) return
          const newMessage = await Promise.all(
            messages.map(async (msg) => {
              const collab = await msg.collaboration?.get()
              if (collab !== undefined) {
                msg.collaborationModel = collab.data() as Collaboration
                msg.collaborationModel.id = collab.id
              }
              return msg
            })
          )
          const reversed = newMessage.reverse()
          lc.setItemLC(lc.LCName.SelectedChat, reversed)
          return reversed
        })
      }
    }

    return await ChatMessage.fetchMessage(
      collaborationId,
      roomsId,
      lastMessage,
      callback
    ).then(async (messages) => {
      if (!messages) return
      const newMessage = await Promise.all(
        messages.map(async (msg) => {
          const collab = await msg.collaboration?.get()
          if (collab !== undefined) {
            msg.collaborationModel = collab.data() as Collaboration
            msg.collaborationModel.id = collab.id
          }
          return msg
        })
      )
      const reversed = newMessage.reverse()
      lc.setItemLC(lc.LCName.SelectedChat, reversed)
      return reversed
    })
  }

  /**
   * Fetch previous messages (pagination)
   * Migrated from: actions/chat.fetchCollaborationPrevMessage
   */
  async fetchPreviousMessages(
    collaborationId: string,
    lastMessage: any,
    callback: any,
    roomId?: string
  ): Promise<Message[]> {
    const messages = await ChatMessage.fetchMessagePrev(
      collaborationId,
      lastMessage,
      callback,
      roomId
    )

    if (messages) {
      const prevMessage = await Promise.all(messages.map(async (msg) => msg))
      return prevMessage.reverse()
    }
    return []
  }

  /**
   * Clear unread messages for collaboration
   * Migrated from: actions/chat.clearUnreadMessages
   */
  async clearUnreadMessages(collaborationId: string): Promise<void> {
    await ChatMessage.updateUnreadMessages(collaborationId)
  }

  /**
   * Clear unread messages for all chat
   * Migrated from: actions/chat.clearUnreadMessagesAllChat
   */
  async clearUnreadMessagesAllChat(collaborationId: string): Promise<void> {
    await ChatMessage.updateUnreadMessagesAllChat(collaborationId)
  }

  /**
   * Clear user unread messages
   * Migrated from: actions/chat.clearUserUnreadMessages
   */
  async clearUserUnreadMessages(collaborationId: string): Promise<void> {
    await ChatMessage.updateUserUnreadMessages(collaborationId)
  }

  /**
   * Update message as read
   * Migrated from: actions/chat.updateMessageRead
   */
  async updateMessageRead(message: Message): Promise<void> {
    await ChatMessage.updateMessageasRead(message)
  }

  // ============================================================================
  // FRIEND COLLABORATION METHODS
  // ============================================================================

  /**
   * Create friend collaboration message
   * Migrated from: actions/chat.createFriendCollaborationMessage
   */
  async createFriendCollaborationMessage(
    newMessage: Message,
    collaborationId: string,
    toUserId: string,
    callback: any
  ): Promise<void> {
    if (newMessage !== undefined) {
      ChatMessage.createMessage(newMessage).then(async (response) => {
        if (response && response !== '') {
          newMessage.id = response
          callback(newMessage, null)
        }
      })
      ChatMessage.increaseUnreadMessage(collaborationId, toUserId)
    }
  }
}

export default CollaborationServiceImpl.getInstance()
